import { Alert, Box, Button, Card, Center, Group, Loader, SegmentedControl, Stack, Text, ThemeIcon, Title, UnstyledButton, type MantineBreakpoint } from '@mantine/core';
import { TrayIcon, WarningCircleIcon, type Icon, type IconWeight } from '@phosphor-icons/react';
import type { Lang } from '@testpio/shared';
import { useId, type ReactNode } from 'react';
import { useLangStore } from '../i18n/lang';
import { errorMessage } from '../lib/api';

/* ------------------------------------------------------------------ brand */

/**
 * The Testpio mark: three rising rank chevrons, the top one saffron: a rank going up.
 * Keep in sync with public/favicon.svg.
 */
export function BrandMark({ size = 32, title }: { size?: number; title?: string }) {
  const id = useId().replace(/[^a-zA-Z0-9_-]/g, '');
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      role={title ? 'img' : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
      style={{ flex: '0 0 auto', display: 'block' }}
    >
      <defs>
        <linearGradient id={`${id}bg`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#3D4FB8" />
          <stop offset="1" stopColor="#1E2A78" />
        </linearGradient>
        <linearGradient id={`${id}sf`} x1="0" y1="1" x2="0" y2="0">
          <stop offset="0" stopColor="#FF8F00" />
          <stop offset="1" stopColor="#FFB547" />
        </linearGradient>
      </defs>
      <rect width="64" height="64" rx="16" fill={`url(#${id}bg)`} />
      <g fill="none" strokeWidth="6.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M19 47 32 38.5 45 47" stroke="#fff" strokeOpacity="0.5" />
        <path d="M19 36 32 27.5 45 36" stroke="#fff" />
        <path d="M19 25 32 16.5 45 25" stroke={`url(#${id}sf)`} />
      </g>
    </svg>
  );
}

/** The Testpio logo: mark + wordmark. `white` is for dark or brand-coloured backgrounds. */
export function Logo({ size = 32, tone = 'default', wordmark = true }: { size?: number; tone?: 'default' | 'white'; wordmark?: boolean }) {
  return (
    <span className="ur-logo" data-tone={tone} style={{ gap: Math.round(size * 0.3), fontSize: Math.round(size * 0.66) }}>
      <BrandMark size={size} title={wordmark ? undefined : 'Testpio'} />
      {wordmark && (
        <span className="ur-logo__word">
          Test<span>pio</span>
        </span>
      )}
    </span>
  );
}

function initialsOf(name: string): string {
  const words = name
    .replace(/[^\p{L}\p{N}\p{M}\s]/gu, ' ')
    .split(/\s+/)
    .filter(Boolean);
  return words
    .slice(0, 2)
    .map((w) => Array.from(w)[0]?.toLocaleUpperCase() ?? '')
    .join('');
}

/** An institute's own logo, or its initials when it has not uploaded one. */
export function InstituteMark({ name, logoUrl, size = 32 }: { name?: string | undefined; logoUrl?: string | null | undefined; size?: number }) {
  const radius = Math.round(size * 0.28);
  if (logoUrl) return <img src={logoUrl} alt="" width={size} height={size} className="institute-mark" style={{ borderRadius: radius }} />;
  const initials = initialsOf(name ?? '') || 'U';
  return (
    <span
      className="institute-mark institute-mark--initials"
      aria-hidden="true"
      style={{ width: size, height: size, borderRadius: radius, fontSize: Math.round(size * (initials.length > 1 ? 0.38 : 0.46)) }}
    >
      {initials}
    </span>
  );
}

/** Institute identity: its mark + name (+ a small subtitle). */
export function Brand({ name, logoUrl, subtitle, size = 32 }: { name?: string | undefined; logoUrl?: string | null | undefined; subtitle?: string; size?: number }) {
  return (
    <Group gap={10} wrap="nowrap" style={{ minWidth: 0 }}>
      <InstituteMark name={name} logoUrl={logoUrl} size={size} />
      <Box style={{ minWidth: 0 }}>
        <Text fw={700} size="md" lh={1.25} truncate c="gray.9">
          {name ?? 'Testpio'}
        </Text>
        {subtitle && (
          <Text size="xs" c="dimmed" lh={1.3} truncate>
            {subtitle}
          </Text>
        )}
      </Box>
    </Group>
  );
}

/* ------------------------------------------------------------------ layout */

/** The menu button for phone headers: three rounded bars that turn into a cross when open. */
export function MenuButton({ opened, onClick, hiddenFrom }: { opened: boolean; onClick: () => void; hiddenFrom?: MantineBreakpoint }) {
  return (
    <UnstyledButton className="menu-button" data-opened={opened} aria-label={opened ? 'Close menu' : 'Open menu'} aria-expanded={opened} onClick={onClick} hiddenFrom={hiddenFrom}>
      <span className="menu-button__bars" aria-hidden="true">
        <span />
        <span />
        <span />
      </span>
    </UnstyledButton>
  );
}

/** Page title row. Actions sit on the title line; in narrow spaces they move below the subtitle. */
export function PageHeader({ title, subtitle, actions, eyebrow }: { title: ReactNode; subtitle?: ReactNode; actions?: ReactNode; eyebrow?: ReactNode }) {
  return (
    <div className="page-header-wrap">
      <div className="page-header">
        {eyebrow && <div className="eyebrow page-header__eyebrow">{eyebrow}</div>}
        <Title order={1} size="h2" className="page-header__title">
          {title}
        </Title>
        {actions && <div className="page-header__actions">{actions}</div>}
        {subtitle && (
          <Text component="div" c="dimmed" size="sm" className="page-header__subtitle">
            {subtitle}
          </Text>
        )}
      </div>
    </div>
  );
}

/** A card with a titled header row. The building block of most pages. */
export function SectionCard({
  title,
  description,
  actions,
  children,
  padding = 'lg',
  icon: IconComponent,
}: {
  title?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  padding?: 'xs' | 'sm' | 'md' | 'lg' | 0;
  icon?: Icon;
}) {
  return (
    <Card padding={0}>
      {(title || actions) && (
        <Group justify="space-between" align="center" px="lg" pt="md" pb={description ? 'sm' : 'xs'} gap="sm" wrap="nowrap">
          <Group gap="sm" wrap="nowrap" align="center" style={{ minWidth: 0 }}>
            {IconComponent && (
              <ThemeIcon size={36} color="brand" style={{ flex: '0 0 auto' }}>
                <IconComponent size={19} weight="duotone" />
              </ThemeIcon>
            )}
            <Box style={{ minWidth: 0 }}>
              {title && (
                <Text fw={700} size="md" lh={1.35}>
                  {title}
                </Text>
              )}
              {description && (
                <Text component="div" size="xs" c="dimmed" mt={2} lh={1.45}>
                  {description}
                </Text>
              )}
            </Box>
          </Group>
          {actions && (
            <Group gap="xs" wrap="nowrap" style={{ flex: '0 0 auto' }}>
              {actions}
            </Group>
          )}
        </Group>
      )}
      <Box p={padding} pt={title || actions ? (padding === 0 ? 0 : 'sm') : padding}>
        {children}
      </Box>
    </Card>
  );
}

/* ------------------------------------------------------------------ states */

export function FullPageLoader({ label }: { label?: string }) {
  return (
    <Center h="60vh">
      <Stack align="center" gap="sm">
        <Loader size="md" type="dots" />
        {label && (
          <Text c="dimmed" size="sm">
            {label}
          </Text>
        )}
      </Stack>
    </Center>
  );
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  return (
    <Alert color="red" variant="light" icon={<WarningCircleIcon size={20} weight="duotone" />} title="Something went wrong" my="md" radius="md">
      <Stack gap="xs" align="flex-start">
        <Text size="sm">{errorMessage(error)}</Text>
        {onRetry && (
          <Button size="xs" variant="white" color="red" onClick={onRetry}>
            Try again
          </Button>
        )}
      </Stack>
    </Alert>
  );
}

export function EmptyState({ title, text, action, icon: IconComponent = TrayIcon }: { title: string; text?: string; action?: ReactNode; icon?: Icon }) {
  return (
    <Card py={48}>
      <Stack align="center" gap="sm" ta="center">
        <ThemeIcon size={64} radius="xl" color="brand" variant="light">
          <IconComponent size={32} weight="duotone" />
        </ThemeIcon>
        <Text fw={700} size="lg" mt={4}>
          {title}
        </Text>
        {text && (
          <Text c="dimmed" size="sm" maw={440}>
            {text}
          </Text>
        )}
        {action && <Box mt="xs">{action}</Box>}
      </Stack>
    </Card>
  );
}

/* ------------------------------------------------------------------ data */

/** A metric tile: label and icon on one centred row, the value below. */
export function StatCard({ label, value, hint, color = 'brand', icon: IconComponent }: { label: string; value: ReactNode; hint?: ReactNode; color?: string; icon?: Icon }) {
  return (
    <Card padding="md">
      <Group justify="space-between" align="center" wrap="nowrap" gap={8}>
        <Text size="sm" c="gray.7" fw={600} lh={1.3} style={{ minWidth: 0 }}>
          {label}
        </Text>
        {IconComponent && (
          <ThemeIcon size={32} color={color} variant="light" radius="md" style={{ flex: '0 0 auto' }}>
            <IconComponent size={18} weight="duotone" />
          </ThemeIcon>
        )}
      </Group>
      <Text fw={700} fz={{ base: 24, sm: 28 }} lh={1.1} mt={10} c="gray.9" className="tabular" style={{ whiteSpace: 'nowrap', letterSpacing: '-0.02em' }}>
        {value}
      </Text>
      {hint && (
        <Text component="div" size="xs" c="dimmed" mt={6}>
          {hint}
        </Text>
      )}
    </Card>
  );
}

const ICON_SIZE = { xs: 14, sm: 16, md: 18, lg: 20 } as const;

/**
 * An icon followed by text. The icon box is exactly one line tall and the row is top-aligned,
 * so the icon stays centred on the first line when the text wraps, in Hindi and English alike.
 */
export function IconText({
  icon: IconComponent,
  children,
  size = 'sm',
  c,
  fw,
  gap = 6,
  iconSize,
  iconWeight,
  iconColor,
  className,
}: {
  icon: Icon;
  children: ReactNode;
  size?: keyof typeof ICON_SIZE;
  c?: string;
  fw?: number;
  gap?: number;
  iconSize?: number;
  iconWeight?: IconWeight;
  iconColor?: string;
  className?: string;
}) {
  return (
    <Text component="div" size={size} c={c} fw={fw} className={className ? `icon-text ${className}` : 'icon-text'} style={{ columnGap: gap }}>
      <span className="icon-text__icon" style={iconColor ? { color: iconColor } : undefined}>
        <IconComponent size={iconSize ?? ICON_SIZE[size]} weight={iconWeight} />
      </span>
      <span className="icon-text__label">{children}</span>
    </Text>
  );
}

/** A small coloured dot + label, for states like Live / Scheduled / Draft. */
export function StatusPill({ color, children, pulse }: { color: string; children: ReactNode; pulse?: boolean }) {
  return (
    <span
      className="status-pill"
      style={{
        background: `var(--mantine-color-${color}-0)`,
        color: `var(--mantine-color-${color}-8)`,
      }}
    >
      <span
        className="status-pill__dot"
        style={{
          background: `var(--mantine-color-${color}-6)`,
          boxShadow: pulse ? `0 0 0 3px var(--mantine-color-${color}-1)` : undefined,
        }}
      />
      {children}
    </span>
  );
}

export function LanguageToggle({ size = 'xs' }: { size?: 'xs' | 'sm' }) {
  const lang = useLangStore((s) => s.lang);
  const setLang = useLangStore((s) => s.setLang);
  return (
    <SegmentedControl
      size={size}
      value={lang}
      onChange={(v) => setLang(v as Lang)}
      data={[
        { label: 'हिं', value: 'hi' },
        { label: 'EN', value: 'en' },
      ]}
      aria-label="Language"
    />
  );
}

/** Multi-line plain text (questions keep their line breaks). */
export function PlainText({ text, size = 'md', fw }: { text: string; size?: 'sm' | 'md' | 'lg'; fw?: number }) {
  return (
    <Text size={size} fw={fw} style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.65 }}>
      {text}
    </Text>
  );
}
